import { useEffect, useState } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { readChangeStream } from './api.js';

/**
 * Live updates: refresh what is on screen as soon as something changes,
 * instead of up to 30 seconds later (see liveChanges.ts on the server).
 *
 * The stream says only WHAT changed (a group, a ticket); this refetches the
 * affected queries through the normal API. Only queries on screen refetch —
 * that is what invalidation does.
 *
 * POLLING STAYS AS THE SAFETY NET. While live, the default interval drops to
 * five minutes; when the stream drops it goes back to 30 seconds until it is
 * back. A person with nothing to follow (a requester) gets no stream, and
 * their screens poll exactly as before.
 */

/** Screens about tickets in general: any change may move them. */
const LIST_KEYS = new Set([
  'tickets',
  'queue-counts',
  'queue-health',
  'new-assignments',
  'overview',
  'daylight-stalls',
  'my-tickets',
  'admin-mail-intake',
]);
const POLL_LIVE_MS = 5 * 60_000;
const POLL_DEFAULT_MS = 30_000;
/** Signals arriving together (an SLA sweep, a merge) become one refresh. */
const BATCH_MS = 400;

function setPolling(qc: QueryClient, ms: number): void {
  const current = qc.getDefaultOptions();
  qc.setDefaultOptions({ ...current, queries: { ...current.queries, refetchInterval: ms } });
}

export function useLiveUpdates(enabled: boolean): boolean {
  const qc = useQueryClient();
  const [live, setLive] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    const stop = new AbortController();
    let changed = new Set<string>();
    let flush: ReturnType<typeof setTimeout> | null = null;

    const refresh = () => {
      flush = null;
      const tenants = changed;
      changed = new Set();
      void qc.invalidateQueries({
        predicate: (q) => {
          const [head, tenant] = q.queryKey as unknown[];
          if (typeof head !== 'string') return false;
          if (LIST_KEYS.has(head)) return true;
          // A ticket page, for a group that changed.
          return head === 'ticket' && typeof tenant === 'string' && tenants.has(tenant);
        },
      });
    };

    void (async () => {
      let wait = 1_000;
      while (!stop.signal.aborted) {
        let outcome: 'ended' | 'none' | 'refused' | 'error' = 'error';
        try {
          outcome = await readChangeStream((event, data) => {
            if (event === 'ready') {
              wait = 1_000;
              setLive(true);
              setPolling(qc, POLL_LIVE_MS);
              return;
            }
            if (event !== 'change') return;
            try {
              const c = JSON.parse(data) as { t?: string };
              if (c.t) changed.add(c.t);
            } catch {
              return;
            }
            flush ??= setTimeout(refresh, BATCH_MS);
          }, stop.signal);
        } catch {
          outcome = 'error';
        }
        setLive(false);
        setPolling(qc, POLL_DEFAULT_MS);
        if (stop.signal.aborted || outcome === 'none') return;
        // Anything missed while it was down is picked up now.
        void qc.invalidateQueries({ predicate: (q) => LIST_KEYS.has(String(q.queryKey[0])) });
        await new Promise((r) => setTimeout(r, wait));
        wait = Math.min(wait * 2, 30_000);
      }
    })();

    return () => {
      stop.abort();
      if (flush) clearTimeout(flush);
      setPolling(qc, POLL_DEFAULT_MS);
      setLive(false);
    };
  }, [enabled, qc]);

  return live;
}
